import type {Context, ReviewPacket} from './model.ts'

/** Context identity is independent of its editable title and experiment labels. */
export const isContextId = (id: string) => /^context-[a-f\d]{8}(?:-[a-f\d]{4}){3}-[a-f\d]{12}$/i.test(id)
export const createContextId = (uuid: () => string = () => crypto.randomUUID()) => `context-${uuid()}`
export function matchesContext(context: { id: string; aliases?: string[] }, id: string | null | undefined) {
  return context.id === id || Boolean(id && context.aliases?.includes(id))
}

/** Resolve cached review names through explicit synced aliases, never editable titles.
 * This is a read-only projection: old review packets and generation evidence stay intact. */
export function canonicalContextPacket(
  packet: ReviewPacket,
  identities: {id:string;aliases?:string[]}[],
): ReviewPacket {
  const owners = new Map<string,string>()
  for (const identity of identities) {
    owners.set(identity.id,identity.id)
    for (const alias of identity.aliases ?? []) owners.set(alias,identity.id)
  }
  const groups = new Map<string,Context[]>()
  for (const context of packet.contexts) {
    const id=owners.get(context.id) ?? context.id
    groups.set(id,[...(groups.get(id) ?? []),context])
  }
  return {...packet,contexts:[...groups].map(([id,copies])=>{
    const base=copies.find(context=>context.id===id) ?? copies[0]!
    const aliases=[...new Set([...copies.flatMap(c=>[...(c.id===id?[]:[c.id]),...(c.aliases??[])]),...identities.filter(i=>i.id===id).flatMap(i=>i.aliases??[])])].filter(alias=>alias!==id)
    // Only authored contributions survive a superseded duplicate's generated layout.
    const lenses=copies.length > 1 ? structuredClone(base.lenses) : base.lenses
    for(const copy of copies) if(copy!==base) for(const lens of copy.lenses) for(const section of lens.sections) {
      const authored=section.contributions.filter(item=>item.origin==='manual')
      if(!authored.length)continue
      let target=lenses.find(item=>item.id===lens.id)
      if(!target){target={...lens,sections:[]};lenses.push(target)}
      let slot=target.sections.find(item=>item.id===section.id)
      if(!slot){slot={...section,contributions:[]};target.sections.push(slot)}
      slot.contributions.push(...authored.filter(item=>!slot!.contributions.some(existing=>existing.id===item.id)))
    }
    return {...base,id,aliases,lenses}
  })}
}
